(ns app.front.view.business.information
  "Business info form, both editor and read-only"
  (:require [app.domain.business.model :as business-model]
            [app.front.form.personal-id-render :as personal-id-render]
            [app.front.ui :as ui]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.party.common :as common]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(rf/reg-sub ::business-types
  :<- [::front-config/config]
  :-> :business-types)

(defn- actual-attrs [business-type editing-existing? foreign-address?]
  (->> business-model/business-attrs
       (remove (if editing-existing?
                 (constantly false)
                 business-model/business-attrs-edit-only))
       (remove #{(if (business-model/personal-id-enabled? {:business/type business-type})
                   :business/id
                   :business/personal-id)
                 (when-not foreign-address? :business/mailing-country)})))

(defn select-input [{:keys [attr required tr values val->str on-change value-fn allow-empty? label-text light]}]
  [ui-select/select {:id           (str attr)
                     :light        (boolean light)
                     :required     required
                     :label-text   (or label-text (tr [attr]))
                     :values       values
                     :val->str     val->str
                     :value        (value-fn attr)
                     :class-name   "full-width-select"
                     :select-text  (tr [:select/select])
                     :on-change    #(on-change attr %)
                     :allow-empty? (if (some? allow-empty?) allow-empty? true)}])

(defn localized-string-input [{:keys [tr on-change-sync value-fn attr required light on-save-needed]}]
  [ui-i18n/localized-string-edit
   {:label-text          (tr [attr])
    :placeholder         (tr [attr])
    :on-change           #(on-change-sync attr %)
    ;; auto-save on blur (only when changed) for callers that opt into per-field saving,
    ;; e.g. editing an existing business in the application wizard
    :on-end-edit         (when on-save-needed #(on-save-needed attr %))
    :allowed-locales-sub ::ui-i18n/languages
    :strings             (value-fn attr)
    :light               (boolean light)
    :invalid             false
    :required            required
    :auto-focus          false}])

(defn text-input [{:keys [field
                          placeholder
                          tr
                          validator
                          required?
                          on-save-needed
                          on-change-sync
                          value-fn
                          label-text
                          light]}]
  [ui/text-input (-> {:id          field
                      :type        :text
                      :light       (boolean light)
                      :disabled    false
                      :label-text  (or label-text (tr [field]))
                      :placeholder (tr [placeholder])
                      :required    (or required? false)
                      :value       (or (value-fn field) "")
                      :on-change   #(let [v (futil/tval %)]
                                      (on-change-sync field v))
                      :on-blur     #(common/execute-if-specified on-save-needed field (futil/tval %))}
                     (common/add-invalid-prop tr validator field))])

(defn business-info-form
  "Business details editor for business page and party modal. Use `:edit-context :add-new` when not editing an
   existing business and `:edit-existing` otherwise - we try to guide the user to add only specific details
   for new business entities and leave other - such as contact email - available but not preferable."
  [{:keys [validator tr schema on-change on-change-sync value-fn existing-business light on-save-needed]}]
  [:<>
   [:div.place-self-end
    [ui-i18n/edit-language-selector {:allowed-locales-sub ::ui-i18n/default-language}]]
   ;; `on-save-needed` is optional: when supplied, inputs auto-save per field (text/phone on blur,
   ;; localized strings on end-of-edit). Existing callers omit it and keep the deferred-save behaviour.
   (let [input-ctx        (util/mmap validator tr on-change-sync value-fn on-save-needed)
         business-type    (value-fn :business/type)
         address-feature? (<sub [::ff/enabled? :feature-flag.id/business-foreign-address])
         business-attrs   (actual-attrs business-type
                                        (some? existing-business)
                                        address-feature?)
         country-subdivs  (<sub [::front-config/address-countries])
         selected-country (value-fn :business/mailing-country)]
     [:div.flex.flex-row.flex-wrap.-mx-2.mt-2.gap-y-3
      (for-frag [k business-attrs
                 :let [required (contains? schema (s/required-key k))]]
        [:div.w-full.sm:w-1-2.lg:w-1-3.px-2.my-2 {:class (when address-feature? "xxl:w-1-4")}
         (case k
           :business/type
           [select-input (util/mmap {:attr      k
                                     :values    (<sub [::business-types])
                                     :val->str  (comp tr vector)
                                     :on-change (fn [attr v]
                                                  (on-change attr v)
                                                  (on-change (if (= v :business.type/sole-proprietorship)
                                                               :business/id
                                                               :business/personal-id)
                                                             nil))
                                     :light     light}
                                    tr required value-fn)]
           :business/mailing-country
           [select-input (util/mmap {:attr         k
                                     :required     true
                                     :allow-empty? false
                                     :values       (keys country-subdivs)
                                     :val->str     #(tr [(keyword "country" (name %))])
                                     :on-change    (fn [attr v]
                                                     (on-change attr v)
                                                     (on-change :business/subdivision nil))}
                                    tr value-fn light)]

           :business/subdivision
           [select-input (util/mmap {:attr         k
                                     :allow-empty? false
                                     :values       (get country-subdivs selected-country)
                                     :val->str     #(tr [(keyword "subdivision" (name %))])
                                     :label-text   (tr [(keyword "subdivision-type" selected-country)])}
                                    tr required on-change value-fn light)]

           :business/postal-code
           [text-input (merge input-ctx {:field       k
                                         :placeholder k
                                         :required?   required
                                         :light       light
                                         :label-text  (tr [(keyword "postal-code-type" selected-country)])})]

           :business/phone
           [ui/intl-tel-input {:id              (str name)
                               :type            :text
                               :label-text      (tr [:business/phone])
                               :placeholder     (tr [:placeholder/international-phone])
                               :required        required
                               :light           light
                               :value           (or (value-fn :business/phone) "")
                               :callback        #(on-change-sync :business/phone %)
                               :on-blur         #(common/execute-if-specified on-save-needed :business/phone %)
                               :initial-country (<sub [::front-config/country])}]

           :business/names
           [localized-string-input (util/mmap {:attr k :required true} tr on-change-sync value-fn light on-save-needed)]

           :business/trade-names
           [localized-string-input (util/mmap {:attr k} tr on-change-sync value-fn light on-save-needed)]

           :business/personal-id
           [:div.flex.items-start.gap-2
            [text-input (merge input-ctx
                               {:field          k
                                :light          light
                                :placeholder    k
                                :required?      required
                                :on-change-sync (fn [attr v]
                                                  (on-change-sync attr v)
                                                  (>evt [::common/set-doc-value [::common/show-errors?] true]))})]
            (when (and existing-business
                       (<sub [::personal-id-render/reveal-visible? (value-fn :business/personal-id)]))
              [:div.mt-6
               (cc/button {:on-click #(>evt [::personal-id-render/reveal-full-id
                                             (:db/id existing-business)
                                             (partial on-change-sync :business/personal-id)])
                           :kind     :ghost
                           :size     :field}
                          (tr [:form-field/personal-id-reveal]))])]

           [text-input (merge input-ctx {:field       k
                                         :placeholder k
                                         :required?   required
                                         :light       light})])])])])

(defn personal-id-display [{:keys [tr business]}]
  [:<>
   [:span (:business/personal-id business)]
   (when (<sub [::personal-id-render/reveal-visible? (:business/personal-id business)])
     [:button.bg-transparent.border-0.p-0.text-ui-blue.hover:underline.ml-2
      {:on-click #(>evt [::personal-id-render/reveal-full-id (:db/id business)])}
      (tr [:form-field/personal-id-reveal])])])


(defn read-only-form [tr {:business/keys [type] :as business}]
  (let [lang             (<sub [:language])
        address-feature? (<sub [::ff/enabled? :feature-flag.id/business-foreign-address])]
    [:div.flex.flex-row.flex-wrap.-mx-2.mt-2.gap-y-3
     (for-frag [k (actual-attrs type false address-feature?)]
       [:div.w-full.sm:w-1-2.lg:w-1-3.px-2.my-2 {:class (when address-feature? "xxl:w-1-4")}
        (let [v (k business)]
          (when (or (not (contains? business-model/business-attrs-edit-only k)) v)
            [:<>
             [:div.bx--label (tr [k])]
             [:div.label-value
              (when v
                (case k
                  :business/names (i18n-model/get-text-from-entities-or-map v lang)
                  :business/trade-names (i18n-model/get-text-from-entities-or-map v lang)
                  :business/type (tr [v])
                  :business/mailing-country (tr [(keyword "country" (name v))])
                  :business/subdivision (tr [(keyword "subdivision" (name v))])
                  :business/personal-id [personal-id-display (util/mmap tr business)]
                  v))]]))])]))
